import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import React from 'react';
import { buildRecruitLink } from './alliance-apps';
import { AlliancePerson } from './alliance-identity';
import { STATUS_COLORS } from './alliance-tokens';
import { useIsMobileLayout } from './bar-controls';
import { MENU_COLORS } from './bar-menu';
import { CURRENT_APP } from './current-app';
import { QrCode } from './qr-code';

/** App avatar colors: the accent, with white text unless the accent is white */
export const appAvatarColors = () => {
  const isWhite = /^#f{3}(f{3})?$/i.test(CURRENT_APP.accent);
  return {
    background: CURRENT_APP.accent,
    color: isWhite ? CURRENT_APP.bar : '#ffffff',
  };
};

const RELATION_STYLES = {
  self: { border: MENU_COLORS.divider, background: 'transparent', chip: '' },
  staff: {
    border: `${STATUS_COLORS.staff.main}80`,
    background: `${STATUS_COLORS.staff.main}12`,
    chip: 'Staff for',
  },
  viewing: {
    border: `${STATUS_COLORS.impersonating.main}80`,
    background: `${STATUS_COLORS.impersonating.main}12`,
    chip: 'Viewing as',
  },
  downline: {
    border: `${STATUS_COLORS.impersonating.main}80`,
    background: `${STATUS_COLORS.impersonating.main}12`,
    chip: 'Your downline',
  },
};

/**
 * "Recruit an agent": a collapsible user-menu section with the agent's
 * Contracting sign-up link, Copy and a QR code. The link sets whose upline
 * the new agent lands under, so the agent is always named when it isn't the
 * signed-in user.
 *
 * @param agent - The agent whose NAA number goes in the link
 * @param relation - self (agent), staff (staff for that agent), viewing
 *   (admin or staff impersonating) or downline (upline agent impersonating)
 */
export function RecruitLink(props: {
  agent: AlliancePerson;
  relation: 'self' | 'staff' | 'viewing' | 'downline';
}) {
  // Props
  const { agent, relation } = props;
  // State
  const [open, setOpen] = React.useState(false);
  const [showQr, setShowQr] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  // Hooks
  const isMobile = useIsMobileLayout();

  React.useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const link = buildRecruitLink({ agentNumber: agent.id });
  const style = RELATION_STYLES[relation];
  const avatar = appAvatarColors();
  const isOther = relation !== 'self';
  const chipColors =
    relation === 'staff'
      ? {
          background: STATUS_COLORS.staff.main,
          color: STATUS_COLORS.staff.onMain,
        }
      : {
          background: STATUS_COLORS.impersonating.main,
          color: STATUS_COLORS.impersonating.onMain,
        };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch (error) {
      // Clipboard can be blocked (insecure context, permissions). Fall back
      // to selecting the link text so the user can copy it by hand.
      const node = document.getElementById('recruit-link-text');
      const selection = window.getSelection();
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const qrSize = isMobile ? 200 : 208;

  return (
    <Box
      sx={{
        m: '0 16px 12px',
        borderRadius: '12px',
        border: `1px solid ${style.border}`,
        backgroundColor: style.background,
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        sx={{
          fontFamily: 'inherit',
          width: '100%',
          height: 48,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          p: '0 12px 0 10px',
          justifyContent: 'flex-start',
          '&:hover': { backgroundColor: MENU_COLORS.hover },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            borderRadius: '8px',
            backgroundColor: avatar.background,
            color: avatar.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonAddIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ fontSize: 15, fontWeight: 500, flex: 1, textAlign: 'left' }}>
          Recruit an agent
        </Box>
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
        )}
      </ButtonBase>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            p: '2px 12px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap',
              fontSize: 13,
              lineHeight: 1.4,
              color: MENU_COLORS.text2,
            }}
          >
            <span>Upline set to</span>
            <Box
              component="span"
              sx={{ fontWeight: 500, color: MENU_COLORS.text }}
            >
              {isOther ? agent.fullName : 'you'}
            </Box>
            {isOther ? (
              <React.Fragment>
                <Box
                  component="span"
                  sx={{
                    fontFamily: "'Roboto Mono', monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: MENU_COLORS.text,
                  }}
                >
                  {agent.id}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    borderRadius: '6px',
                    p: '2px 6px',
                    ...chipColors,
                  }}
                >
                  {style.chip}
                </Box>
              </React.Fragment>
            ) : null}
          </Box>

          <Box
            id="recruit-link-text"
            sx={{
              p: '10px 12px',
              borderRadius: '10px',
              border: `1px solid ${MENU_COLORS.divider}`,
              backgroundColor: MENU_COLORS.hover,
              fontFamily: "'Roboto Mono', monospace",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.5,
              wordBreak: 'break-all',
            }}
          >
            {link.replace('https://', '')}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <ButtonBase
              onClick={handleCopy}
              sx={{
                fontFamily: 'inherit',
                flex: 1,
                height: 40,
                gap: '6px',
                borderRadius: '10px',
                backgroundColor: avatar.background,
                color: avatar.color,
                fontSize: 14,
                fontWeight: 500,
                '&:hover': { filter: 'brightness(1.08)' },
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 18 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              )}
              {copied ? 'Copied' : 'Copy link'}
            </ButtonBase>
            <ButtonBase
              aria-pressed={showQr}
              onClick={() => setShowQr((current) => !current)}
              sx={{
                fontFamily: 'inherit',
                flex: 1,
                height: 40,
                gap: '6px',
                borderRadius: '10px',
                border: `1px solid ${MENU_COLORS.divider}`,
                backgroundColor: showQr ? MENU_COLORS.hover : 'transparent',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': { backgroundColor: MENU_COLORS.hover },
              }}
            >
              <QrCode2Icon sx={{ fontSize: 20 }} />
              QR code
            </ButtonBase>
          </Box>

          {showQr ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                pt: '4px',
              }}
            >
              <Box
                sx={{
                  width: qrSize,
                  height: qrSize,
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  p: '10px',
                  boxSizing: 'border-box',
                  border: '1px solid #0000001a',
                  display: 'flex',
                }}
              >
                <QrCode
                  value={link}
                  size={qrSize - 22}
                  logoColor={avatar.background}
                  title={`Sign-up link for ${agent.fullName}`}
                />
              </Box>
              <Box
                sx={{
                  fontSize: 13,
                  textAlign: 'center',
                  color: MENU_COLORS.text2,
                  maxWidth: 240,
                }}
              >
                Scan to sign up under {agent.fullName} ({agent.id})
              </Box>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
